// src/pages/components/ProfileComp/ReferralPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { styled } from "@mui/material/styles";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../../components/firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

const REF_REWARD = 250;

const GLASS = {
  surface: "linear-gradient(180deg, rgba(12,16,22,.92) 0%, rgba(8,11,17,.92) 60%)",
  border: "1px solid rgba(255,255,255,.08)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const Section = styled(Card)({
  borderRadius: 12,
  background: GLASS.surface,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
});

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(String(text || ""));
  } catch {}
}

function buildReferralLink(code) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (!code) return "";
  // если у тебя реальный маршрут регистрации другой — поменяй тут
  return `${origin}/signup?ref=${encodeURIComponent(code)}`;
}

function formatTs(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
}

export default function ReferralPanel({ userDoc }) {
  const [user] = useAuthState(auth);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const referralCode = String(userDoc?.referralCode || "").trim();
  const referralLink = useMemo(() => buildReferralLink(referralCode), [referralCode]);

  useEffect(() => {
    if (!user?.uid) return;

    setLoading(true);

    const ref = collection(db, "referrals", user.uid, "referred");
    const q = query(ref, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const out = [];
        snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
        setRows(out);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return () => unsub();
  }, [user?.uid]);

  const confirmedCount = useMemo(
    () => rows.filter((r) => String(r.status || "pending") === "confirmed").length,
    [rows]
  );

  const pendingCount = useMemo(
    () => rows.length - confirmedCount,
    [rows, confirmedCount]
  );

  // ВАЖНО: это "витринный" расчет (MVP/Prod-lite) — reward считается по confirmedCount.
  // Если хочешь 100% совпадение с фактом начисления — считай по milesEvents (source=referral).
  const totalReward = useMemo(
    () => confirmedCount * REF_REWARD,
    [confirmedCount]
  );

  if (!user) {
    return (
      <Section sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 900 }}>Not authenticated</Typography>
      </Section>
    );
  }

  return (
    <Section>
      <CardContent>
        <Typography sx={{ fontWeight: 1000, fontSize: 16, mb: 0.8 }}>
          Referrals
        </Typography>

        <Typography sx={{ color: GLASS.subtext, fontSize: 13, lineHeight: 1.7 }}>
          Invite users with your referral link. Rewards are shown based on confirmed referrals.
        </Typography>

        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.08)" }} />

        {/* Code + Link */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <Box>
            <Typography sx={{ color: GLASS.subtext, fontSize: 12, fontWeight: 900 }}>
              Referral code
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.6, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 1000, color: GLASS.text, fontSize: 14 }}>
                {referralCode || "No code yet"}
              </Typography>

              {referralCode ? (
                <Tooltip title="Copy code">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(referralCode)}
                    sx={{ color: GLASS.text }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>

            <Typography sx={{ color: GLASS.subtext, fontSize: 12, mt: 1.2 }}>
              Code should be issued by backend to prevent manipulation.
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ color: GLASS.subtext, fontSize: 12, fontWeight: 900 }}>
              Referral link
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.6, flexWrap: "wrap" }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: GLASS.text,
                  fontSize: 13,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={referralLink || ""}
              >
                {referralLink || "—"}
              </Typography>

              {referralLink ? (
                <>
                  <Tooltip title="Copy link">
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(referralLink)}
                      sx={{ color: GLASS.text }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Open">
                    <IconButton
                      size="small"
                      onClick={() => window.open(referralLink, "_blank")}
                      sx={{ color: GLASS.text }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : null}
            </Box>
          </Box>
        </Box>

        {/* Summary */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
          <Chip
            size="small"
            label={`pending: ${pendingCount}`}
            sx={{
              color: GLASS.text,
              border: "1px solid rgba(255,255,255,.12)",
              bgcolor: "rgba(255,255,255,.04)",
              fontWeight: 900,
            }}
          />
          <Chip
            size="small"
            label={`confirmed: ${confirmedCount}`}
            sx={{
              color: GLASS.text,
              border: "1px solid rgba(255,255,255,.12)",
              bgcolor: "rgba(255,255,255,.04)",
              fontWeight: 900,
            }}
          />
          <Chip
            size="small"
            label={`reward: ${totalReward}`}
            sx={{
              color: GLASS.text,
              border: "1px solid rgba(255,255,255,.12)",
              bgcolor: "rgba(255,255,255,.04)",
              fontWeight: 900,
            }}
          />
        </Box>

        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.08)" }} />

        {/* List */}
        <Typography sx={{ fontWeight: 950, color: GLASS.text, mb: 1 }}>
          Invited users
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography sx={{ color: GLASS.subtext, fontWeight: 800 }}>
              Loading…
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ color: GLASS.subtext, fontSize: 13 }}>
            No referrals yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map((r) => {
              const status = String(r.status || "pending");
              const createdAt = formatTs(r.createdAt);
              const confirmedAt = formatTs(r.confirmedAt);

              return (
                <Box
                  key={r.id}
                  sx={{
                    border: "1px solid rgba(255,255,255,.10)",
                    background: "rgba(255,255,255,.03)",
                    borderRadius: 10,
                    p: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950, color: GLASS.text, fontSize: 13 }}>
                      {r.id}
                    </Typography>

                    <Typography sx={{ color: GLASS.subtext, fontSize: 12 }}>
                      created: {createdAt || "—"}
                      {confirmedAt ? ` • confirmed: ${confirmedAt}` : ""}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={status}
                    sx={{
                      height: 22,
                      borderRadius: 1,
                      fontWeight: 900,
                      bgcolor:
                        status === "confirmed"
                          ? "rgba(20,241,149,.14)"
                          : "rgba(56,189,248,.10)",
                      color: GLASS.text,
                      border: "1px solid rgba(255,255,255,.12)",
                      "& .MuiChip-label": { px: 0.75, fontSize: 11, lineHeight: "22px", fontWeight: 900 },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Section>
  );
}
